import React, { useState, useEffect } from 'react';
import { Loader2, Trash2, Pencil, ShieldCheck, User as UserIcon, Crown, Search, X as XIcon } from 'lucide-react';
import api from '../lib/api';
import { useLang, tx } from '../i18n/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

const UsersManager = () => {
  const { lang, isRTL } = useLang();
  const { user: currentUser } = useAuth();
  const { toast } = useToast();
  const L = (ar, en) => tx(lang, ar, en);

  const [users, setUsers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [u, p] = await Promise.all([api.get('/admin/users'), api.get('/plans')]);
      setUsers(u.data || []);
      setPlans(p.data || []);
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to load users' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openEdit = (u) => {
    setEditing(u.user_id);
    setForm({
      name: u.name || '',
      role: u.role || 'user',
      plan_id: u.subscription?.plan_id || '',
      status: u.subscription?.status || 'active',
      expires_at: u.subscription?.expires_at?.slice(0, 10) || '',
    });
  };

  const close = () => { setEditing(null); setForm({}); };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        name: form.name,
        role: form.role,
      };
      if (form.plan_id) {
        payload.subscription = {
          plan_id: form.plan_id,
          status: form.status,
          expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : '',
        };
      }
      const { data } = await api.patch(`/admin/users/${editing}`, payload);
      setUsers(users.map(u => u.user_id === editing ? data : u));
      // Re-fetch to enrich subscription with plan name
      load();
      toast({ title: L('تم التحديث', 'Updated') });
      close();
    } catch (err) {
      toast({ title: 'Error', description: err?.response?.data?.detail || 'Failed' });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (user_id, email) => {
    if (!window.confirm(L(`هل أنت متأكد من حذف ${email}؟`, `Delete ${email}?`))) return;
    try {
      await api.delete(`/admin/users/${user_id}`);
      setUsers(users.filter(u => u.user_id !== user_id));
      toast({ title: L('تم الحذف', 'Deleted') });
    } catch (err) {
      toast({ title: 'Error', description: err?.response?.data?.detail || 'Failed' });
    }
  };

  const removeSub = async (user_id) => {
    if (!window.confirm(L('إلغاء اشتراك المستخدم؟', 'Remove user subscription?'))) return;
    try {
      await api.delete(`/admin/users/${user_id}/subscription`);
      load();
      toast({ title: L('تم إلغاء الاشتراك', 'Subscription removed') });
    } catch {}
  };

  const filtered = users.filter(u => {
    const t = (u.email + ' ' + (u.name || '')).toLowerCase();
    return t.includes(q.toLowerCase());
  });

  const stats = {
    total: users.length,
    admins: users.filter(u => u.role === 'admin').length,
    subscribers: users.filter(u => u.subscription?.plan_id).length,
  };

  return (
    <div>
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-5">
        <div className="rounded-xl bg-white border border-slate-200 p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center"><UserIcon className="w-5 h-5 text-blue-600" /></div>
          <div><div className="text-xl font-extrabold text-slate-900">{stats.total}</div><div className="text-xs text-slate-500">{L('إجمالي المستخدمين', 'Total users')}</div></div>
        </div>
        <div className="rounded-xl bg-white border border-slate-200 p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-amber-500/15 flex items-center justify-center"><ShieldCheck className="w-5 h-5 text-amber-600" /></div>
          <div><div className="text-xl font-extrabold text-slate-900">{stats.admins}</div><div className="text-xs text-slate-500">{L('أدمن', 'Admins')}</div></div>
        </div>
        <div className="rounded-xl bg-white border border-slate-200 p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-green-500/15 flex items-center justify-center"><Crown className="w-5 h-5 text-green-600" /></div>
          <div><div className="text-xl font-extrabold text-slate-900">{stats.subscribers}</div><div className="text-xs text-slate-500">{L('مشتركين فعّالين', 'Active subscribers')}</div></div>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-4">
        <div className="flex-1 relative">
          <Search className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 ${isRTL ? 'right-3' : 'left-3'}`} />
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder={L('ابحث بالبريد أو الاسم...', 'Search by email or name...')} className={`bg-white border-slate-200 text-slate-900 h-11 ${isRTL ? 'pr-9' : 'pl-9'}`} />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>
      ) : (
        <div className="rounded-xl bg-white border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className={`p-3 text-slate-600 font-semibold ${isRTL ? 'text-right' : 'text-left'}`}>{L('المستخدم', 'User')}</th>
                <th className={`p-3 text-slate-600 font-semibold ${isRTL ? 'text-right' : 'text-left'}`}>{L('البريد', 'Email')}</th>
                <th className={`p-3 text-slate-600 font-semibold ${isRTL ? 'text-right' : 'text-left'}`}>{L('الدور', 'Role')}</th>
                <th className={`p-3 text-slate-600 font-semibold ${isRTL ? 'text-right' : 'text-left'}`}>{L('الاشتراك', 'Subscription')}</th>
                <th className={`p-3 text-slate-600 font-semibold ${isRTL ? 'text-right' : 'text-left'}`}>{L('الحالة', 'Status')}</th>
                <th className={`p-3 text-slate-600 font-semibold ${isRTL ? 'text-right' : 'text-left'}`}>{L('انضم في', 'Joined')}</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="p-6 text-center text-slate-500">{L('لا يوجد مستخدمين', 'No users')}</td></tr>
              )}
              {filtered.map(u => {
                const sub = u.subscription || {};
                const planName = lang === 'ar' ? sub.plan_nameAr : sub.plan_nameEn;
                return (
                  <tr key={u.user_id} className="border-b border-blue-900/20 hover:bg-blue-900/10">
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        {u.picture ? (
                          <img src={u.picture} alt="" className="w-8 h-8 rounded-full" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-slate-900 font-bold text-xs">{(u.name || u.email || '?').charAt(0).toUpperCase()}</div>
                        )}
                        <span className="text-slate-900 font-medium">{u.name || '-'}</span>
                      </div>
                    </td>
                    <td className="p-3 text-slate-600" dir="ltr">{u.email}</td>
                    <td className="p-3">
                      {u.role === 'admin' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-600 text-xs border border-amber-500/30">
                          <ShieldCheck className="w-3 h-3" /> {L('أدمن', 'Admin')}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-xs border border-blue-200">
                          <UserIcon className="w-3 h-3" /> {L('مستخدم', 'User')}
                        </span>
                      )}
                    </td>
                    <td className="p-3 text-slate-700">
                      {planName ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-500/15 text-green-700 text-xs border border-green-500/30">
                          <Crown className="w-3 h-3" /> {planName}
                        </span>
                      ) : <span className="text-slate-500 text-xs">{L('بدون اشتراك', 'No subscription')}</span>}
                    </td>
                    <td className="p-3">
                      {sub.status ? (
                        <span className={`px-2 py-0.5 rounded-full text-xs ${sub.status === 'active' ? 'bg-green-500/15 text-green-600' : 'bg-red-500/15 text-red-600'}`}>
                          {sub.status === 'active' ? L('نشط', 'Active') : sub.status}
                        </span>
                      ) : <span className="text-slate-600">-</span>}
                    </td>
                    <td className="p-3 text-slate-500 text-xs" dir="ltr">{u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}</td>
                    <td className="p-3">
                      <div className="flex gap-1 justify-end">
                        <Button onClick={() => openEdit(u)} variant="outline" className="border-blue-200 text-blue-700 hover:bg-slate-100 h-8 px-2"><Pencil className="w-3.5 h-3.5" /></Button>
                        {sub.plan_id && (
                          <Button onClick={() => removeSub(u.user_id)} variant="outline" className="border-orange-500/40 text-orange-600 hover:bg-orange-500/10 h-8 px-2" title={L('إلغاء الاشتراك', 'Remove subscription')}><XIcon className="w-3.5 h-3.5" /></Button>
                        )}
                        <Button onClick={() => remove(u.user_id, u.email)} disabled={u.user_id === currentUser?.user_id || u.email === 'admin@automation.com'} variant="outline" className="border-red-500/40 text-red-600 hover:bg-red-500/10 h-8 px-2 disabled:opacity-30 disabled:cursor-not-allowed"><Trash2 className="w-3.5 h-3.5" /></Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && close()}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-slate-900 text-xl">{L('تعديل المستخدم', 'Edit User')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-slate-600 text-sm mb-1.5 block">{L('الاسم', 'Name')}</label>
              <Input required value={form.name || ''} onChange={e => setForm({ ...form, name: e.target.value })} className="bg-slate-50 border-slate-200 text-slate-900 h-11" />
            </div>
            <div>
              <label className="text-slate-600 text-sm mb-1.5 block">{L('الدور', 'Role')}</label>
              <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} className="w-full bg-slate-50 border border-slate-200 text-slate-900 h-11 rounded-md px-3">
                <option value="user">{L('مستخدم', 'User')}</option>
                <option value="admin">{L('أدمن', 'Admin')}</option>
              </select>
            </div>
            <div>
              <label className="text-slate-600 text-sm mb-1.5 block">{L('الباقة', 'Plan')}</label>
              <select value={form.plan_id || ''} onChange={e => setForm({ ...form, plan_id: e.target.value })} className="w-full bg-slate-50 border border-slate-200 text-slate-900 h-11 rounded-md px-3">
                <option value="">{L('بدون اشتراك', 'No subscription')}</option>
                {plans.map(p => (
                  <option key={p.id} value={p.id}>{lang === 'ar' ? p.nameAr : p.nameEn} (${p.price} USD)</option>
                ))}
              </select>
            </div>
            {form.plan_id && (
              <>
                <div>
                  <label className="text-slate-600 text-sm mb-1.5 block">{L('حالة الاشتراك', 'Status')}</label>
                  <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} className="w-full bg-slate-50 border border-slate-200 text-slate-900 h-11 rounded-md px-3">
                    <option value="active">{L('نشط', 'Active')}</option>
                    <option value="paused">{L('موقوف', 'Paused')}</option>
                    <option value="expired">{L('منتهي', 'Expired')}</option>
                  </select>
                </div>
                <div>
                  <label className="text-slate-600 text-sm mb-1.5 block">{L('تاريخ الانتهاء', 'Expires at')}</label>
                  <Input type="date" value={form.expires_at} onChange={e => setForm({ ...form, expires_at: e.target.value })} className="bg-slate-50 border-slate-200 text-slate-900 h-11" />
                </div>
              </>
            )}
            <div className="flex gap-2 pt-2">
              <Button type="submit" disabled={busy} className="btn-primary-blue border-0 h-11 px-7 font-semibold">
                {busy ? <Loader2 className="w-4 h-4 animate-spin me-2" /> : null}
                {L('حفظ', 'Save')}
              </Button>
              <Button type="button" onClick={close} variant="outline" className="border-slate-300 text-slate-700 hover:bg-slate-100 h-11">{L('إلغاء', 'Cancel')}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersManager;
